import { baseApi } from '@/lib/utils'

// Mirrors controller/leaveBalance.controller.js on the backend — "consumed"
// and "balance" are computed server-side from approved leave_requests, not
// stored, so they always reflect the latest ClickUp-synced approval state.
export interface LeaveBalance {
  id: number
  employee_id: number
  employee_name: string
  leave_type: string
  total_assigned: number
  start_date: string
  end_date: string
  status: 'active' | 'inactive'
  consumed: number
  balance: number
  created_at: string
  updated_at: string
}

export interface GetLeaveBalancesParams {
  employee_id?: number
  leave_type?: string
  status?: 'active' | 'inactive'
  page?: number
  limit?: number
}

export interface LeaveBalancesResponse {
  success: boolean
  data: LeaveBalance[]
  pagination: {
    total: number
    page: number
    limit: number
    totalPages: number
  }
}

export interface CreateLeaveBalancePayload {
  employee_id: number
  leave_type: string
  total_assigned: number
  start_date: string
  end_date: string
  status?: 'active' | 'inactive'
}

export interface UpdateLeaveBalancePayload {
  id: number
  leave_type?: string
  total_assigned?: number
  start_date?: string
  end_date?: string
  status?: 'active' | 'inactive'
}

export const leaveBalanceApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getLeaveBalances: builder.query<
      LeaveBalancesResponse,
      GetLeaveBalancesParams
    >({
      query: (params = {}) => ({
        url: '/leave-balances',
        params,
      }),
      providesTags: ['LeaveBalances'],
    }),

    createLeaveBalance: builder.mutation<
      { success: boolean; message: string },
      CreateLeaveBalancePayload
    >({
      query: payload => ({
        url: '/leave-balances',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['LeaveBalances'],
    }),

    updateLeaveBalance: builder.mutation<
      { success: boolean; message: string },
      UpdateLeaveBalancePayload
    >({
      query: ({ id, ...body }) => ({
        url: `/leave-balances/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['LeaveBalances'],
    }),

    deleteLeaveBalance: builder.mutation<
      { success: boolean; message: string },
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/leave-balances/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['LeaveBalances'],
    }),
  }),
})

export const {
  useGetLeaveBalancesQuery,
  useCreateLeaveBalanceMutation,
  useUpdateLeaveBalanceMutation,
  useDeleteLeaveBalanceMutation,
} = leaveBalanceApi
